import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PMDialog } from "../../utils/PMDialog";
import { PromangRichTextEditor } from "../../utils/PromangRichTextEditor";
import { ProjectSelector } from "../../utils/moduleSelectors/projectSelector";
import { WorkOrderSelector } from "../../utils/moduleSelectors/workOrderSelector";
import { ModernProjectActivitySelectorField } from "../../utils/moduleSelectors/ModernProjectActivitySelectorField";
import { activityChipLabel } from "../../utils/moduleSelectors/GroupedActivityChecklist";
import { EmployeeSelector } from "../../utils/moduleSelectors/employeeSelector";
import axios from "../../../api/axios";
import {
  buildLinksPayload,
  defaultResourceTimes,
  fetchProjectEventConflicts,
  parseEventLinks,
  appendWorkOrderDescription,
  addWorkOrderLinks,
  addActivityLinks,
  removeLink,
  setPrimaryLink,
  formatLocalDateTimeInput,
} from "../../../api/projectPlan";
import { ProjectEventResourceHours } from "./projectEventResourceHours";
import { ExclamationTriangleIcon, PlusIcon, StarIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { StarIcon as StarIconSolid } from "@heroicons/react/24/solid";

const formatLocalDateTime = formatLocalDateTimeInput;

const PlanLinkChip = ({ label, isPrimary, primaryLabel, setPrimaryLabel, onRemove, onSetPrimary }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium ${
      isPrimary
        ? "border-teal-400 bg-teal-50 text-teal-900"
        : "border-slate-200 bg-slate-50 text-slate-800"
    }`}
  >
    <button
      type="button"
      title={isPrimary ? primaryLabel : setPrimaryLabel}
      onClick={onSetPrimary}
      className={isPrimary ? "text-teal-600" : "text-slate-400 hover:text-teal-600"}
    >
      {isPrimary ? (
        <StarIconSolid className="h-4 w-4" />
      ) : (
        <StarIcon className="h-4 w-4" />
      )}
    </button>
    {isPrimary ? (
      <span className="rounded bg-teal-200/80 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
        {primaryLabel}
      </span>
    ) : null}
    <span className="max-w-[200px] truncate">{label}</span>
    <button type="button" onClick={onRemove} className="text-slate-500 hover:text-red-600">
      <XMarkIcon className="h-3.5 w-3.5" />
    </button>
  </span>
);

export const ProjectEventDialog = ({
  open,
  onClose,
  event,
  project,
  prefillDate,
  prefillWorkOrder = null,
  onSaved,
  mode = "full",
}) => {
  const { t } = useTranslation();
  const isPlanMode = mode === "plan";
  const isScheduleMode = mode === "schedule";
  const isFullMode = mode === "full";
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    start_date: "",
    end_date: "",
    days: "",
    project_id: project?.id || null,
    project: project || null,
  });
  const [workOrderLinks, setWorkOrderLinks] = useState([]);
  const [activityLinks, setActivityLinks] = useState([]);
  const [resources, setResources] = useState([]);
  const [errors, setErrors] = useState({});
  const [resourceConflicts, setResourceConflicts] = useState({});
  const [includeWoDescription, setIncludeWoDescription] = useState(true);
  const conflictTimer = useRef(null);

  useEffect(() => {
    if (!open) return;

    if (event) {
      const startDate = event.start_date
        ? event.start_date.substring(0, 10)
        : isScheduleMode
          ? prefillDate || ""
          : "";
      const endDate = event.end_date
        ? event.end_date.substring(0, 10)
        : isScheduleMode
          ? prefillDate || startDate || ""
          : "";
      let days = "";
      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        days = Math.ceil(Math.abs(end - start) / (1000 * 60 * 60 * 24)).toString();
      }

      const parsed = parseEventLinks(event);
      setWorkOrderLinks(parsed.workOrderLinks);
      setActivityLinks(parsed.activityLinks);
      setFormData({
        title: event.title || "",
        description: event.description || "",
        start_date: startDate,
        end_date: endDate,
        days,
        project_id: event.project_id || project?.id || null,
        project: event.project || project || null,
      });

      if (event.resources?.length) {
        setResources(
          event.resources.map((r) => ({
            id: r.id || null,
            user_id: r.user_id,
            user: r.user || (r.user_id ? { id: r.user_id, displayTitle: "…" } : null),
            start_time: formatLocalDateTime(r.start_time),
            end_time: formatLocalDateTime(r.end_time),
          }))
        );
      } else {
        setResources([]);
      }
    } else if (prefillWorkOrder?.id) {
      const woLinks = addWorkOrderLinks([], [prefillWorkOrder]);
      let paLinks = [];
      if (prefillWorkOrder.project_activity) {
        paLinks = addActivityLinks([], [prefillWorkOrder.project_activity]);
      } else if (prefillWorkOrder.project_activity_id) {
        paLinks = addActivityLinks([], [
          {
            id: prefillWorkOrder.project_activity_id,
            display_title: `#${prefillWorkOrder.project_activity_id}`,
          },
        ]);
      }
      setWorkOrderLinks(woLinks);
      setActivityLinks(paLinks);
      setFormData({
        title:
          prefillWorkOrder.title ||
          prefillWorkOrder.displayTitle ||
          "",
        description: prefillWorkOrder.description || "",
        start_date: isPlanMode ? "" : prefillDate || "",
        end_date: isPlanMode ? "" : prefillDate || "",
        days: isPlanMode ? "" : prefillDate ? "0" : "",
        project_id: prefillWorkOrder.project_id || project?.id || null,
        project: prefillWorkOrder.project || project || null,
      });
      setResources([]);
    } else {
      setWorkOrderLinks([]);
      setActivityLinks([]);
      setFormData({
        title: "",
        description: "",
        start_date: isPlanMode ? "" : prefillDate || "",
        end_date: isPlanMode ? "" : prefillDate || "",
        days: isPlanMode ? "" : prefillDate ? "0" : "",
        project_id: project?.id || null,
        project: project || null,
      });
      setResources([]);
    }
    setIncludeWoDescription(true);
    setErrors({});
    setResourceConflicts({});
  }, [event, project, prefillDate, prefillWorkOrder, open, isPlanMode, isScheduleMode]);

  const dialogTitle = useMemo(() => {
    if (isPlanMode) {
      return event ? t("project.plan.edit_plan") : t("project.plan.create_plan");
    }
    if (isScheduleMode) {
      return t("project.plan.schedule_plan");
    }
    return event ? t("project.event.edit") : t("project.event.create");
  }, [isPlanMode, isScheduleMode, event, t]);

  const checkResourceConflict = useCallback((index, resource) => {
    if (!resource?.user_id || !resource.start_time || !resource.end_time) {
      setResourceConflicts((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
      return;
    }
    clearTimeout(conflictTimer.current);
    conflictTimer.current = setTimeout(async () => {
      try {
        const startIso = new Date(resource.start_time).toISOString();
        const endIso = new Date(resource.end_time).toISOString();
        const res = await fetchProjectEventConflicts({
          user_ids: resource.user_id,
          start_time: startIso,
          end_time: endIso,
          exclude_resource_id: resource.id,
          ...(event?.id ? { exclude_project_event_id: event.id } : {}),
        });
        const count = (res.data?.plan?.length || 0) + (res.data?.calendar?.length || 0);
        setResourceConflicts((prev) => ({
          ...prev,
          [index]: count > 0 ? res.data : null,
        }));
      } catch {
        /* ignore */
      }
    }, 400);
  }, [event?.id]);

  const validate = () => {
    const newErrors = {};
    if (!formData.title?.trim()) newErrors.title = t("project.event.title_required");
    if (!isPlanMode && !formData.start_date) {
      newErrors.start_date = t("project.event.start_date_required");
    }
    if (formData.end_date && formData.start_date && formData.end_date < formData.start_date) {
      newErrors.end_date = t("project.event.end_date_invalid");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const addResource = (presetHours, dateOverride) => {
    // Never fall back to today — user must pick the day (or we reuse a date
    // they already chose: explicit override, form start, or clicked prefill).
    const date = dateOverride || formData.start_date || prefillDate || null;
    if (!date) {
      setResources((prev) => [
        ...prev,
        { user_id: null, user: null, start_time: "", end_time: "" },
      ]);
      return;
    }
    const [startH, endH] = presetHours || [8, 16];
    const times = defaultResourceTimes(date, startH, endH);
    setResources((prev) => [
      ...prev,
      { user_id: null, user: null, start_time: times.start_time, end_time: times.end_time },
    ]);
  };

  const removeResource = (index) => {
    setResources((prev) => prev.filter((_, i) => i !== index));
    setResourceConflicts((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const updateResource = (index, patch) => {
    setResources((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], ...patch };
      checkResourceConflict(index, updated[index]);
      return updated;
    });
  };

  const applyDateToResources = () => {
    if (!formData.start_date) return;
    setResources((prev) =>
      prev.map((r) => {
        const startH = r.start_time ? new Date(r.start_time).getHours() : 8;
        const endH = r.end_time ? new Date(r.end_time).getHours() : 16;
        const times = defaultResourceTimes(formData.start_date, startH, endH || startH + 8);
        return { ...r, start_time: times.start_time, end_time: times.end_time };
      })
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setErrors({});

    try {
      const url = event ? `/project_events/${event.id}` : "/project_events";
      const method = event ? "put" : "post";

      const validResources = isPlanMode
        ? []
        : resources
            .filter((r) => r.user_id && r.start_time)
            .map((r) => ({
              id: r.id || null,
              user_id: Number(r.user_id),
              start_time: r.start_time,
              end_time: r.end_time || r.start_time,
            }));

      const primaryWo = workOrderLinks.find((l) => l.isPrimary)?.record || workOrderLinks[0]?.record;
      const primaryPa = activityLinks.find((l) => l.isPrimary)?.record || activityLinks[0]?.record;
      const links = buildLinksPayload(workOrderLinks, activityLinks);
      const eventStatus = isPlanMode ? "draft" : "scheduled";

      const payload = {
        project_event: {
          title: formData.title,
          description: formData.description,
          start_date: isPlanMode ? null : formData.start_date || null,
          end_date: isPlanMode ? null : formData.end_date || formData.start_date || null,
          status: eventStatus,
          project_id: formData.project_id || project?.id,
          work_order_id: primaryWo?.id || null,
          project_activity_id: primaryPa?.id || null,
          links,
          resources: validResources,
        },
      };

      const response = await axios({ method, url, data: payload });
      if (response.data) {
        onSaved?.(response.data);
        onClose();
      }
    } catch (error) {
      if (error.response?.data?.errors) {
        if (Array.isArray(error.response.data.errors)) {
          setErrors({ general: error.response.data.errors.join(", ") });
        } else {
          setErrors(error.response.data.errors);
        }
      } else {
        setErrors({ general: error.response?.data?.error || error.message || t("project.event.save_error") });
      }
    } finally {
      setLoading(false);
    }
  };

  const hasAnyConflict = Object.values(resourceConflicts).some(Boolean);

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={dialogTitle}
      size="4xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {errors.general && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            <p className="text-sm font-medium">{errors.general}</p>
          </div>
        )}

        {isScheduleMode && event ? (
          <div className="rounded-lg border border-teal-200 bg-teal-50 px-4 py-3 text-sm text-teal-900">
            <p className="font-medium">{event.title || formData.title}</p>
            {(workOrderLinks.length > 0 || activityLinks.length > 0) && (
              <p className="mt-1 text-teal-800">
                {workOrderLinks.length > 0
                  ? `${workOrderLinks.length} ${t("project.event.work_order").toLowerCase()}`
                  : ""}
                {workOrderLinks.length > 0 && activityLinks.length > 0 ? " · " : ""}
                {activityLinks.length > 0
                  ? `${activityLinks.length} ${t("project_activity.label").toLowerCase()}`
                  : ""}
              </p>
            )}
          </div>
        ) : null}

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            {t("project.event.title")} *
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.title ? "border-red-500" : "border-gray-300"
            }`}
            placeholder={t("project.event.title_placeholder")}
          />
          {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
        </div>

        {!isScheduleMode && (
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            {t("project.event.description")}
          </label>
          <PromangRichTextEditor
            value={formData.description || ""}
            onChange={(html) => setFormData((prev) => ({ ...prev, description: html }))}
            size="md"
            framed
            templatesEnabled={false}
          />
        </div>
        )}

        {!isPlanMode && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {t("project.event.start_date")} *
            </label>
            <input
              type="date"
              value={formData.start_date}
              onChange={(e) => {
                const newStartDate = e.target.value;
                let newEndDate = formData.end_date || newStartDate;
                let newDays = formData.days;
                if (newStartDate && newDays && !Number.isNaN(parseInt(newDays, 10))) {
                  const start = new Date(newStartDate);
                  start.setDate(start.getDate() + parseInt(newDays, 10));
                  newEndDate = start.toISOString().substring(0, 10);
                }
                setFormData((prev) => ({
                  ...prev,
                  start_date: newStartDate,
                  end_date: newEndDate,
                  days: newDays,
                }));
              }}
              className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.start_date ? "border-red-500" : "border-gray-300"
              }`}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {t("project.event.days")}
            </label>
            <input
              type="number"
              min="0"
              value={formData.days}
              onChange={(e) => {
                const daysValue = e.target.value;
                let newEndDate = formData.end_date;
                if (formData.start_date && daysValue && !Number.isNaN(parseInt(daysValue, 10))) {
                  const start = new Date(formData.start_date);
                  start.setDate(start.getDate() + parseInt(daysValue, 10));
                  newEndDate = start.toISOString().substring(0, 10);
                }
                setFormData((prev) => ({ ...prev, days: daysValue, end_date: newEndDate }));
              }}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {t("project.event.end_date")}
            </label>
            <input
              type="date"
              value={formData.end_date}
              onChange={(e) => setFormData((prev) => ({ ...prev, end_date: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        )}

        {!isScheduleMode && !project && (
          <ProjectSelector
            name="project_id"
            label={t("project.event.project")}
            selected={formData.project}
            onChange={(data) => {
              const newId = data.target.value;
              setFormData((prev) => ({
                ...prev,
                project_id: newId,
                project: data.target.object || null,
              }));
              if (String(formData.project_id) !== String(newId)) {
                setWorkOrderLinks([]);
                setActivityLinks([]);
              }
            }}
          />
        )}

        {!isScheduleMode && (
        <>
        <div className="space-y-2 rounded-lg border border-gray-200 p-4">
          <label className="block text-sm font-medium text-gray-700">
            {t("project.event.work_orders_linked")}
          </label>
          <p className="text-xs text-gray-500">{t("project.plan.multi_link_hint")}</p>
          <div className="flex flex-wrap gap-2">
            {workOrderLinks.map(({ record: wo, isPrimary }) => (
              <PlanLinkChip
                key={wo.id}
                label={wo.displayTitle || wo.title || `#${wo.id}`}
                isPrimary={isPrimary}
                primaryLabel={t("project.plan.primary_link")}
                setPrimaryLabel={t("project.plan.set_primary")}
                onRemove={() => setWorkOrderLinks((prev) => removeLink(prev, wo.id))}
                onSetPrimary={() => setWorkOrderLinks((prev) => setPrimaryLink(prev, wo.id))}
              />
            ))}
          </div>
          {workOrderLinks.length === 0 ? (
            <p className="text-xs italic text-gray-500">{t("project.plan.no_work_orders_yet")}</p>
          ) : null}
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={includeWoDescription}
              onChange={(e) => setIncludeWoDescription(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            {t("project.plan.include_work_order_description")}
          </label>
          <WorkOrderSelector
            name="work_order_add"
            label={t("project.event.select_work_orders")}
            multiple
            excludeIds={workOrderLinks.map((l) => l.record.id)}
            project={formData.project_id ? { id: formData.project_id } : null}
            fields="id,displayTitle,title,number,status,description,is_completed"
            onMultipleSelect={(workOrders) => {
              if (!workOrders?.length) return;
              setWorkOrderLinks((prev) => addWorkOrderLinks(prev, workOrders));
              setFormData((prev) => {
                const first = workOrders[0];
                let description = prev.description;
                if (includeWoDescription) {
                  workOrders.forEach((wo) => {
                    if (wo.description) {
                      description = appendWorkOrderDescription(description, wo);
                    }
                  });
                }
                return {
                  ...prev,
                  title: prev.title || first?.title || first?.displayTitle || prev.title,
                  description,
                };
              });
            }}
          />
        </div>

        <div className="space-y-2 rounded-lg border border-gray-200 p-4">
          <label className="block text-sm font-medium text-gray-700">
            {t("project.event.activities_linked")}
          </label>
          <p className="text-xs text-gray-500">{t("project.plan.multi_link_hint")}</p>
          <div className="flex flex-wrap gap-2">
            {activityLinks.map(({ record: pa, isPrimary }) => (
              <PlanLinkChip
                key={pa.id}
                label={activityChipLabel(pa)}
                isPrimary={isPrimary}
                primaryLabel={t("project.plan.primary_link")}
                setPrimaryLabel={t("project.plan.set_primary")}
                onRemove={() => setActivityLinks((prev) => removeLink(prev, pa.id))}
                onSetPrimary={() => setActivityLinks((prev) => setPrimaryLink(prev, pa.id))}
              />
            ))}
          </div>
          {activityLinks.length === 0 ? (
            <p className="text-xs italic text-gray-500">{t("project.plan.no_activities_yet")}</p>
          ) : null}
          <ModernProjectActivitySelectorField
            label={t("project.event.select_activities")}
            multiple
            zIndex={2800}
            excludeIds={activityLinks.map((l) => l.record.id)}
            project={formData.project?.id ? formData.project : formData.project_id ? { id: formData.project_id } : null}
            onMultipleSelect={(activities) => {
              if (!activities?.length) return;
              setActivityLinks((prev) => addActivityLinks(prev, activities));
              const first = activities[0];
              setFormData((prev) => ({
                ...prev,
                title: prev.title || first?.title || first?.display_title || prev.title,
                description: prev.description || first?.description || prev.description,
              }));
            }}
          />
        </div>
        </>
        )}

        {!isPlanMode && (
        <div className="border-t pt-4">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <label className="text-sm font-medium text-gray-700">{t("project.event.resources")}</label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => addResource([8, 16])}
                className="inline-flex items-center rounded-lg bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-100"
              >
                <PlusIcon className="mr-1 h-4 w-4" />
                {t("project.event.add_resource")} 08–16
              </button>
              {formData.start_date ? (
                <button
                  type="button"
                  onClick={() => addResource([8, 16], formData.start_date)}
                  className="inline-flex items-center rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-50"
                >
                  <PlusIcon className="mr-1 h-4 w-4" />
                  {t("project.event.add_resource_one_day")}
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => addResource([7, 15])}
                className="inline-flex items-center rounded-lg bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-100"
              >
                <PlusIcon className="mr-1 h-4 w-4" />
                07–15
              </button>
              {formData.start_date && resources.length > 0 ? (
                <button
                  type="button"
                  onClick={applyDateToResources}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                >
                  {t("project.event.sync_resource_dates")}
                </button>
              ) : null}
            </div>
          </div>

          {hasAnyConflict && (
            <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" />
              <span>{t("project.event.conflict_warning")}</span>
            </div>
          )}

          {resources.length === 0 ? (
            <p className="text-sm italic text-gray-500">{t("project.event.no_resources")}</p>
          ) : (
            <div className="space-y-3">
              {resources.map((resource, index) => {
                const conflict = resourceConflicts[index];
                return (
                  <div
                    key={index}
                    className={`rounded-lg border p-4 ${
                      conflict ? "border-amber-300 bg-amber-50/50" : "border-gray-200 bg-gray-50"
                    }`}
                  >
                    <div className="mb-3 flex items-start justify-between gap-2">
                      <h4 className="text-sm font-medium text-gray-700">
                        {t("project.event.resource")} {index + 1}
                        {resource.start_time ? (
                          <span className="ml-2 font-normal text-gray-500">
                            {resource.start_time.substring(0, 10)}
                          </span>
                        ) : null}
                      </h4>
                      <button type="button" onClick={() => removeResource(index)} className="text-red-600">
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="space-y-3">
                      <EmployeeSelector
                        name={`resource_${index}_user_id`}
                        includeHired
                        label={t("project.event.resource_employee")}
                        selected={
                          resource.user
                            ? {
                                id: resource.user_id,
                                displayTitle: resource.user.displayTitle || resource.user.name,
                              }
                            : null
                        }
                        onChange={(data) => {
                          const userId = data.target.value || data.target.object?.id;
                          const userObject = data.target.object || (userId ? { id: userId } : null);
                          if (userId) {
                            updateResource(index, { user_id: userId, user: userObject });
                          }
                        }}
                      />
                      <ProjectEventResourceHours
                        startTime={resource.start_time}
                        endTime={resource.end_time}
                        onTimesChange={(patch) => updateResource(index, patch)}
                      />
                      {conflict && (
                        <div className="text-xs text-amber-800">
                          {(conflict.plan || []).map((c) => (
                            <div key={`p-${c.id}`}>
                              {t("project.event.conflict_plan", {
                                title: c.title,
                                time: `${c.start_time?.substring(11, 16)}–${c.end_time?.substring(11, 16)}`,
                              })}
                            </div>
                          ))}
                          {(conflict.calendar || []).map((c) => (
                            <div key={`c-${c.id}`}>
                              {t("project.event.conflict_calendar", {
                                title: c.title,
                                time: `${c.start_time?.substring(11, 16)}–${c.end_time?.substring(11, 16)}`,
                              })}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        )}

        <div className="flex justify-end space-x-3 border-t pt-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t("Cancel")}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {loading
              ? t("Lagrer...")
              : isPlanMode
                ? t("project.plan.save_plan")
                : isScheduleMode
                  ? t("project.plan.schedule_confirm")
                  : event
                    ? t("Update")
                    : t("Create")}
          </button>
        </div>
      </form>
    </PMDialog>
  );
};
